'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

type Syntax = 'plain' | 'javascript' | 'typescript' | 'python' | 'bash' | 'json' | 'html' | 'css' | 'sql' | 'markdown';
type Expiry = '1h' | '24h' | '7d' | '30d' | 'never';

interface Paste {
  id: string;
  slug: string;
  title: string;
  content: string;
  syntax: Syntax;
  expiry: Expiry;
  expires_at: string | null;
  view_count: number;
  is_public: boolean;
  created_at: string;
}

type ToastType = 'success' | 'error' | 'info';
interface Toast { id: number; msg: string; type: ToastType }

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = (msg: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  };
  return { toasts, show };
}

const SYNTAX_OPTIONS: { value: Syntax; label: string }[] = [
  { value: 'plain', label: 'Plain text' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'bash', label: 'Bash / Shell' },
  { value: 'json', label: 'JSON' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'sql', label: 'SQL' },
  { value: 'markdown', label: 'Markdown' },
];

const EXPIRY_OPTIONS: { value: Expiry; label: string }[] = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'never', label: 'Never expires' },
];

function timeUntil(dateStr: string | null): string {
  if (!dateStr) return 'Never expires';
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h remaining`;
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m remaining`;
  return `${m}m remaining`;
}

function generateSlug(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function SecurePastePage() {
  const { user, loading } = useAuth();
  const [pastes, setPastes] = useState<Paste[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<'list' | 'create' | 'read'>('list');
  const [activePaste, setActivePaste] = useState<Paste | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toasts, show } = useToast();
  const supabase = createClient();

  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [syntax, setSyntax] = useState<Syntax>('plain');
  const [expiry, setExpiry] = useState<Expiry>('24h');
  const [isPublic, setIsPublic] = useState(true);

  const fetchPastes = useCallback(async () => {
    if (!user) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pastes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!error) setPastes(data ?? []);
    } catch {
      show('Failed to load pastes', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading) fetchPastes();
  }, [user, loading, fetchPastes]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) { show('Paste content cannot be empty', 'error'); return; }
    setIsSubmitting(true);
    try {
      let slug = generateSlug();
      // Ensure unique slug
      const { data: existing } = await supabase.from('pastes').select('id').eq('slug', slug).maybeSingle();
      if (existing) slug = generateSlug() + generateSlug().slice(0, 4);

      const { data, error } = await supabase.from('pastes').insert({
        user_id: user?.id ?? null,
        slug,
        title: title.trim() || 'Untitled Paste',
        content: content.trim(),
        syntax,
        expiry,
        is_public: isPublic,
      }).select().single();

      if (error) throw error;
      show('Paste created!', 'success');
      setPastes((prev) => [data, ...prev]);
      setActivePaste(data);
      setView('read');
      // Reset form
      setTitle(''); setContent(''); setSyntax('plain'); setExpiry('24h'); setIsPublic(true);
    } catch {
      show('Failed to create paste', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (pasteId: string) => {
    try {
      const { error } = await supabase.from('pastes').delete().eq('id', pasteId).eq('user_id', user!.id);
      if (error) throw error;
      setPastes((prev) => prev.filter((p) => p.id !== pasteId));
      if (activePaste?.id === pasteId) { setActivePaste(null); setView('list'); }
      show('Paste deleted', 'info');
    } catch {
      show('Failed to delete paste', 'error');
    }
  };

  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/p/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(slug);
      show('Link copied to clipboard');
      setTimeout(() => setCopiedSlug(null), 2000);
    });
  };

  const copyContent = (text: string) => {
    navigator.clipboard.writeText(text).then(() => show('Content copied'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <div className="glass-card p-8 rounded-2xl flex items-center gap-3">
          <svg className="animate-spin w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-foreground font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mesh">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} className={`px-4 py-3 rounded-xl text-sm font-medium shadow-lg border backdrop-blur-md animate-slide-up pointer-events-auto
            ${t.type === 'success' ? 'bg-emerald-50/90 text-emerald-700 border-emerald-200' :
              t.type === 'error'? 'bg-red-50/90 text-red-700 border-red-200' : 'bg-blue-50/90 text-blue-700 border-blue-200'}`}>
            {t.msg}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="glass-topbar px-6 py-4 flex items-center gap-4 sticky top-0 z-30">
        <Link href="/" className="flex items-center gap-2">
          <div className="glass rounded-lg p-1.5"><AppLogo size={22} /></div>
          <span className="font-bold text-foreground">CompanyInbox</span>
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground font-semibold">SecurePaste</span>
        <div className="ml-auto flex items-center gap-3">
          {user && view !== 'create' && (
            <button onClick={() => setView('create')} className="btn-primary text-sm py-1.5 px-4 flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Paste
            </button>
          )}
          {view !== 'list' && (
            <button onClick={() => { setView('list'); setActivePaste(null); }} className="btn-ghost text-sm py-1.5 px-3">
              ← All Pastes
            </button>
          )}
          <Link href="/inbox-dashboard" className="btn-ghost text-sm py-1.5 px-3">Inbox</Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* ── CREATE VIEW ── */}
        {view === 'create' && (
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">New Paste</h1>
              <p className="text-muted-foreground">Share text, code, or credentials with an expiring link. No account needed to view.</p>
            </div>
            <form onSubmit={handleCreate} className="space-y-5">
              <div className="glass-card rounded-2xl p-6 space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Title <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. API keys for staging, Deploy script..."
                    className="input-field w-full"
                    maxLength={120}
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Content <span className="text-red-500">*</span></label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Paste your text, code, credentials, or notes here..."
                    className="input-field w-full font-mono text-sm resize-none"
                    rows={14}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">{content.length.toLocaleString()} characters</p>
                </div>

                {/* Options row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Syntax</label>
                    <select value={syntax} onChange={(e) => setSyntax(e.target.value as Syntax)} className="input-field w-full text-sm">
                      {SYNTAX_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Expires</label>
                    <select value={expiry} onChange={(e) => setExpiry(e.target.value as Expiry)} className="input-field w-full text-sm">
                      {EXPIRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Visibility</label>
                    <div className="flex items-center gap-3 h-[42px]">
                      <button
                        type="button"
                        onClick={() => setIsPublic(!isPublic)}
                        className={`relative w-11 h-6 rounded-full transition-all duration-200 ${isPublic ? 'bg-primary' : 'bg-gray-200'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${isPublic ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                      <span className="text-sm text-secondary-foreground">{isPublic ? 'Public link' : 'Private'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button type="submit" className="btn-primary px-8 py-3 text-base font-semibold" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating...
                    </span>
                  ) : 'Create Paste →'}
                </button>
                <button type="button" onClick={() => setView('list')} className="btn-ghost px-6 py-3">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ── READ VIEW ── */}
        {view === 'read' && activePaste && (
          <div>
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-1">{activePaste.title}</h1>
                <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                  <span className="font-mono bg-white/60 border border-white/80 px-2 py-0.5 rounded">{activePaste.syntax}</span>
                  <span>{timeUntil(activePaste.expires_at)}</span>
                  <span>{activePaste.view_count} views</span>
                  <span>{activePaste.is_public ? '🔗 Public' : '🔒 Private'}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyLink(activePaste.slug)}
                  className="btn-ghost text-sm py-2 px-4 flex items-center gap-2"
                >
                  {copiedSlug === activePaste.slug ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                    </svg>
                  )}
                  {copiedSlug === activePaste.slug ? 'Copied!' : 'Copy link'}
                </button>
                <button onClick={() => copyContent(activePaste.content)} className="btn-ghost text-sm py-2 px-4">Copy content</button>
                {user && (
                  <button onClick={() => handleDelete(activePaste.id)} className="text-sm py-2 px-4 rounded-xl text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all">
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Shareable URL */}
            <div className="glass rounded-xl p-3 mb-5 flex items-center gap-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
              </svg>
              <span className="font-mono text-sm text-primary flex-1 truncate">
                {typeof window !== 'undefined' ? window.location.origin : 'https://companyinbox.online'}/p/{activePaste.slug}
              </span>
              <button onClick={() => copyLink(activePaste.slug)} className="text-xs font-semibold text-primary hover:text-accent transition-colors flex-shrink-0">
                {copiedSlug === activePaste.slug ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            {/* Content */}
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/50 bg-white/30">
                <span className="text-xs font-mono text-muted-foreground">{activePaste.title}</span>
                <span className="text-xs text-muted-foreground">{activePaste.syntax}</span>
              </div>
              <pre className="p-5 text-sm font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-words leading-relaxed max-h-[60vh] overflow-y-auto scrollbar-thin">
                {activePaste.content}
              </pre>
            </div>
          </div>
        )}

        {/* ── LIST VIEW ── */}
        {view === 'list' && (
          <div>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/8 border border-primary/15 text-xs font-semibold text-primary mb-3">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  SecurePaste
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Your Pastes</h1>
                <p className="text-muted-foreground">Share text, code, and credentials via expiring links. Tied to your inbox session.</p>
              </div>
              {user && (
                <button onClick={() => setView('create')} className="btn-primary px-6 py-3 flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  New Paste
                </button>
              )}
            </div>

            {!user && (
              <div className="glass-card rounded-2xl p-8 text-center mb-8">
                <div className="w-12 h-12 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center mx-auto mb-4">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <p className="font-semibold text-foreground mb-1">Sign in to manage pastes</p>
                <p className="text-sm text-muted-foreground mb-4">Create an account to save, manage, and share pastes tied to your inbox.</p>
                <Link href="/sign-up-login-screen" className="btn-primary">Get started free</Link>
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <svg className="animate-spin w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : pastes.length === 0 && user ? (
              <div className="glass-card rounded-2xl p-10 text-center">
                <div className="w-12 h-12 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center mx-auto mb-4">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <p className="font-semibold text-foreground mb-1">No pastes yet</p>
                <p className="text-sm text-muted-foreground mb-4">Create your first paste to share text, code, or credentials securely.</p>
                <button onClick={() => setView('create')} className="btn-primary">Create first paste</button>
              </div>
            ) : (
              <div className="space-y-3">
                {pastes.map((paste) => {
                  const expired = paste.expires_at && new Date(paste.expires_at) < new Date();
                  return (
                    <div key={paste.id} className={`glass-card rounded-2xl p-4 flex items-start gap-4 ${expired ? 'opacity-60' : ''}`}>
                      <div className="w-9 h-9 rounded-xl bg-primary/8 border border-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-foreground truncate">{paste.title}</span>
                          {expired && <span className="text-xs text-red-500 font-medium">Expired</span>}
                          {!expired && paste.expires_at && (
                            <span className="text-xs text-amber-600 font-medium">{timeUntil(paste.expires_at)}</span>
                          )}
                          {!paste.expires_at && <span className="text-xs text-emerald-600 font-medium">Never expires</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span className="font-mono bg-white/60 border border-white/80 px-1.5 py-0.5 rounded">{paste.syntax}</span>
                          <span>{paste.content.length.toLocaleString()} chars</span>
                          <span>{paste.view_count} views</span>
                          <span className="font-mono text-primary">/p/{paste.slug}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setActivePaste(paste); setView('read'); }}
                          className="p-2 rounded-xl hover:bg-white/60 transition-colors text-muted-foreground hover:text-foreground"
                          title="View paste"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => copyLink(paste.slug)}
                          className="p-2 rounded-xl hover:bg-white/60 transition-colors text-muted-foreground hover:text-foreground"
                          title="Copy link"
                        >
                          {copiedSlug === paste.slug ? (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(paste.id)}
                          className="p-2 rounded-xl hover:bg-red-50 transition-colors text-muted-foreground hover:text-red-500"
                          title="Delete paste"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4h6v2"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
